import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { CalendarDays, MapPin, Repeat, Ticket, ArrowUpRight, ArrowLeft, Clock, Share2 } from "lucide-react";
import { toast } from "sonner";

const FMT = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Australia/Brisbane",
};

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [venue, setVenue] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}`).then(async (r) => {
      setEvent(r.data);
      if (r.data.venue_id) {
        try {
          const v = await api.get(`/venues/${r.data.venue_id}`);
          setVenue(v.data);
        } catch {
          /* no-op */
        }
      }
    });
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text: event.description, url });
        return;
      } catch {
        /* ignored — user cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (!event) return <Layout><div className="py-20 text-center text-neutral-500">Loading...</div></Layout>;

  const img = event.image || "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1800";
  const recurLabel = event.recurrence === "weekly" ? "Every week" : event.recurrence === "biweekly" ? "Every 2 weeks" : "Every month";

  return (
    <Layout>
      <div className="relative h-[55vh] overflow-hidden">
        <img src={img} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        <div className="absolute top-6 left-6">
          <Link to="/events" className="bg-white/90 backdrop-blur rounded-full px-4 py-2 text-sm text-navy inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="back-to-events">
            <ArrowLeft className="w-4 h-4" /> All events
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            {event.is_recurring && (
              <span className="inline-flex items-center gap-1 bg-coastal text-white text-xs px-3 py-1 rounded-full mb-3" data-testid="event-recurring-badge">
                <Repeat className="w-3 h-3" /> {recurLabel}
              </span>
            )}
            <h1 className="font-heading text-4xl lg:text-6xl text-white tracking-tight" data-testid="event-title">{event.title}</h1>
            {event.venue_name && (
              <p className="text-white/80 text-lg mt-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {event.venue_name}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-12">
        {/* Body */}
        <div className="lg:col-span-2">
          <h2 className="font-heading text-2xl text-navy mb-4">About this event</h2>
          <p className="text-neutral-700 leading-relaxed text-lg whitespace-pre-line" data-testid="event-description">
            {event.description || "No description provided."}
          </p>

          {venue && (
            <div className="mt-10 p-6 rounded-2xl bg-white border border-black/5">
              <div className="text-xs uppercase tracking-widest text-coastal mb-1">Venue</div>
              <Link to={`/venue/${venue.id}`} className="font-heading text-2xl text-navy hover:underline underline-offset-4 decoration-coastal/40" data-testid="venue-link">
                {venue.name}
              </Link>
              {venue.address && <p className="text-sm text-neutral-500 mt-1">{venue.address}</p>}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm" data-testid="event-when">
            <div className="text-xs uppercase tracking-widest text-coastal mb-3">When</div>
            <div className="flex items-start gap-3 text-sm">
              <CalendarDays className="w-4 h-4 text-coastal mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-navy">
                  {new Date(event.start_time).toLocaleString("en-AU", FMT)}
                </div>
                {event.end_time && (
                  <div className="text-neutral-500 mt-1 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    until {new Date(event.end_time).toLocaleString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone: "Australia/Brisbane" })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {event.price && (
            <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-coastal mb-2">Price</div>
              <div className="font-heading text-2xl text-navy">{event.price}</div>
            </div>
          )}

          {event.booking_url && (
            <a
              href={event.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center px-6 py-4 rounded-full bg-navy text-white font-medium hover:bg-navy/90 transition-colors"
              data-testid="event-reserve-cta"
            >
              <span className="inline-flex items-center gap-2">
                <Ticket className="w-4 h-4" /> Reserve a spot
                <ArrowUpRight className="w-4 h-4 opacity-70" />
              </span>
            </a>
          )}

          <button
            type="button"
            onClick={share}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white border border-navy/10 text-navy hover:bg-sand/30 text-sm font-medium transition-colors"
            data-testid="event-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share this event
          </button>
        </aside>
      </div>
    </Layout>
  );
}
